import { useState } from "react";
import { C } from "../constants";
import { toDisplayWeight, weightLabel } from "../utils/units";
import { Btn, Badge } from "./ui";

export const ProgramsTab = ({ programs, activeProgramId, onSetActive, onStartPlan, onNewProgram, onEditProgram, onDeleteProgram, onNewPlan, onAddRestDay, onEditPlan, onDeletePlan, onReorderPlans, onReorderPrograms, lastRunPlanId, settings }) => {
  const [expanded,     setExpanded]     = useState(activeProgramId);
  const [draggingProg, setDraggingProg] = useState(null);
  const [dragOverProg, setDragOverProg] = useState(null);

  const toggleExpand = id => setExpanded(e => e === id ? null : id);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <div style={{ color: C.textDim, fontSize: 13 }}>{programs.length} program{programs.length !== 1 ? "s" : ""}</div>
        <Btn variant="primary" onClick={onNewProgram} style={{ padding: "8px 18px" }}>+ New Program</Btn>
      </div>
      <div style={{ color: C.muted, fontSize: 12, marginBottom: 18 }}>
        The <span style={{ color: C.accent }}>active program</span> drives the rotation on your dashboard.
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {programs.map((prog, progIdx) => {
          const isActive   = prog.id === activeProgramId;
          const isExpanded = expanded === prog.id;
          return (
            <div key={prog.id}
              draggable
              onDragStart={e => { setDraggingProg(progIdx); e.dataTransfer.effectAllowed = "move"; }}
              onDragOver={e => { e.preventDefault(); if (progIdx !== draggingProg) setDragOverProg(progIdx); }}
              onDrop={e => { e.preventDefault(); if (draggingProg !== null && draggingProg !== progIdx) onReorderPrograms(draggingProg, progIdx); setDraggingProg(null); setDragOverProg(null); }}
              onDragEnd={() => { setDraggingProg(null); setDragOverProg(null); }}
              style={{
                background: C.card,
                border: `1.5px solid ${isActive ? C.blue : dragOverProg === progIdx ? C.accent + "66" : C.border}`,
                borderRadius: 14, overflow: "hidden",
                opacity: draggingProg === progIdx ? 0.4 : 1,
                transition: "border-color 0.15s",
              }}
            >
              {/* Program header */}
              <div style={{ padding: "14px 18px", cursor: "pointer" }} onClick={() => toggleExpand(prog.id)}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 24 }}>{prog.emoji}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span style={{ fontWeight: 700, fontSize: 16, color: C.text }}>{prog.name}</span>
                      {isActive && (
                        <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 4, background: C.blue + "33", color: C.blue, textTransform: "uppercase", letterSpacing: "0.06em" }}>Active</span>
                      )}
                    </div>
                    {(() => {
                      const workoutCount = prog.plans.filter(p => p.type !== "rest").length;
                      const restCount    = prog.plans.filter(p => p.type === "rest").length;
                      return (
                        <div style={{ color: C.muted, fontSize: 12, marginTop: 2 }}>
                          {workoutCount} workout{workoutCount !== 1 ? "s" : ""}
                          {restCount > 0 && ` · ${restCount} rest`}
                        </div>
                      );
                    })()}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    {!isActive && (
                      <button onClick={e => { e.stopPropagation(); onSetActive(prog.id); }} style={{
                        padding: "5px 12px", borderRadius: 7, border: `1px solid ${C.border}`,
                        background: "transparent", color: C.muted, fontSize: 12, fontWeight: 600, cursor: "pointer",
                      }}>Set Active</button>
                    )}
                    <button onClick={e => { e.stopPropagation(); onEditProgram(prog); }} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 15, padding: "4px 5px" }}>✏️</button>
                    <button onClick={e => { e.stopPropagation(); onDeleteProgram(prog.id); }} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 15, padding: "4px 5px" }}>🗑</button>
                    <div style={{ color: C.muted, fontSize: 18, padding: "0 4px", userSelect: "none" }}>{isExpanded ? "▾" : "▸"}</div>
                  </div>
                </div>
              </div>

              {/* Plans list */}
              {isExpanded && (
                <div style={{ borderTop: `1px solid ${C.border}` }}>
                  {prog.plans.length === 0 ? (
                    <div style={{ padding: "20px", textAlign: "center", color: C.muted, fontSize: 13 }}>No workouts yet — add one below</div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      {prog.plans.map((plan, planIdx) => {
                        const isLastRun = plan.id === lastRunPlanId;
                        const notLast   = planIdx < prog.plans.length - 1;

                        if (plan.type === "rest") {
                          return (
                            <div key={plan.id} style={{
                              padding: "10px 18px",
                              borderBottom: notLast ? `1px solid ${C.border}` : "none",
                              display: "flex", alignItems: "center", gap: 8,
                              background: C.surface + "55",
                            }}>
                              <div style={{ width: 20, height: 20, borderRadius: 5, flexShrink: 0, background: C.surface, border: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: C.muted }}>
                                {planIdx + 1}
                              </div>
                              <span style={{ fontSize: 16 }}>🌙</span>
                              <span style={{ fontSize: 13, color: C.muted, fontStyle: "italic", flex: 1, letterSpacing: "0.02em" }}>Rest Day</span>
                              <button onClick={() => planIdx > 0 && onReorderPlans(prog.id, planIdx, planIdx - 1)}
                                style={{ background: "none", border: "none", cursor: planIdx > 0 ? "pointer" : "default", color: planIdx > 0 ? C.muted : C.border, fontSize: 14, padding: "2px 4px" }}>↑</button>
                              <button onClick={() => planIdx < prog.plans.length - 1 && onReorderPlans(prog.id, planIdx, planIdx + 1)}
                                style={{ background: "none", border: "none", cursor: planIdx < prog.plans.length - 1 ? "pointer" : "default", color: planIdx < prog.plans.length - 1 ? C.muted : C.border, fontSize: 14, padding: "2px 4px" }}>↓</button>
                              <button onClick={() => onDeletePlan(prog.id, plan.id)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, padding: "2px 4px" }}>🗑</button>
                            </div>
                          );
                        }

                        return (
                          <div key={plan.id} style={{
                            padding: "12px 18px",
                            borderBottom: notLast ? `1px solid ${C.border}` : "none",
                            background: isLastRun ? C.blue + "0d" : "transparent",
                          }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                              <div style={{ width: 20, height: 20, borderRadius: 5, flexShrink: 0, background: C.surface, border: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: C.muted }}>
                                {planIdx + 1}
                              </div>
                              <span style={{ fontSize: 18 }}>{plan.emoji}</span>
                              <span style={{ fontWeight: 600, fontSize: 15, color: C.text, flex: 1 }}>{plan.name}</span>
                              {isLastRun && (
                                <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 4, background: C.accent + "22", color: C.accent, textTransform: "uppercase" }}>Last run</span>
                              )}
                              <button onClick={() => planIdx > 0 && onReorderPlans(prog.id, planIdx, planIdx - 1)}
                                style={{ background: "none", border: "none", cursor: planIdx > 0 ? "pointer" : "default", color: planIdx > 0 ? C.muted : C.border, fontSize: 14, padding: "2px 4px" }}>↑</button>
                              <button onClick={() => planIdx < prog.plans.length - 1 && onReorderPlans(prog.id, planIdx, planIdx + 1)}
                                style={{ background: "none", border: "none", cursor: planIdx < prog.plans.length - 1 ? "pointer" : "default", color: planIdx < prog.plans.length - 1 ? C.muted : C.border, fontSize: 14, padding: "2px 4px" }}>↓</button>
                              <button onClick={() => onEditPlan(prog.id, plan)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 14, padding: "2px 4px" }}>✏️</button>
                              <button onClick={() => onDeletePlan(prog.id, plan.id)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 14, padding: "2px 4px" }}>🗑</button>
                            </div>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 8, paddingLeft: 28 }}>
                              {[...new Set(plan.exercises.map(e => e.muscleGroup))].map(g => <Badge key={g}>{g}</Badge>)}
                            </div>
                            <div style={{ paddingLeft: 28 }}>
                              {plan.exercises.map((ex, i) => (
                                <div key={ex.id} style={{ display: "flex", gap: 8, fontSize: 12, color: C.textDim, marginBottom: 3 }}>
                                  <span style={{ color: C.muted, width: 14 }}>{i + 1}.</span>
                                  <span style={{ flex: 1 }}>{ex.name}</span>
                                  <span style={{ color: C.muted }}>{ex.sets}×{ex.reps}{ex.weight > 0 ? ` @ ${toDisplayWeight(ex.weight, settings?.weightUnit)}${weightLabel(settings?.weightUnit)}` : ""}</span>
                                </div>
                              ))}
                            </div>
                            <div style={{ paddingLeft: 28, marginTop: 10 }}>
                              <button
                                onClick={() => plan.exercises.length > 0 && onStartPlan(plan)}
                                disabled={plan.exercises.length === 0}
                                style={{
                                  background: plan.exercises.length > 0 ? C.accent : C.border,
                                  color: plan.exercises.length > 0 ? "#1e1d1b" : C.muted,
                                  border: "none", borderRadius: 8, padding: "8px 20px",
                                  fontWeight: 700, fontSize: 13,
                                  cursor: plan.exercises.length > 0 ? "pointer" : "not-allowed",
                                  opacity: plan.exercises.length === 0 ? 0.5 : 1,
                                }}>
                                {plan.exercises.length === 0 ? "No exercises" : "▶ Start"}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div style={{ padding: "12px 18px", borderTop: `1px solid ${C.border}`, display: "flex", gap: 8 }}>
                    <button onClick={() => onNewPlan(prog.id)} style={{
                      flex: 1, padding: "9px 0", borderRadius: 8,
                      border: `1px dashed ${C.border}`, background: "transparent",
                      color: C.muted, fontSize: 13, fontWeight: 600, cursor: "pointer",
                    }}>+ Add Workout</button>
                    <button onClick={() => onAddRestDay && onAddRestDay(prog.id)} style={{
                      flex: 1, padding: "9px 0", borderRadius: 8,
                      border: `1px dashed ${C.border}`, background: "transparent",
                      color: C.muted, fontSize: 13, fontWeight: 600, cursor: "pointer",
                    }}>🌙 + Rest Day</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {programs.length === 0 && (
          <div style={{ textAlign: "center", padding: "60px 0", color: C.muted }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
            <div style={{ fontSize: 14 }}>No programs yet — create your first one!</div>
          </div>
        )}
      </div>
    </div>
  );
};
